/**
 * WorkDocColorPicker — Color picker using native HTML input
 * (lightweight alternative to react-colorful which has React 19 peer dep issues)
 */
import { useState, useRef, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const PRESET_COLORS = [
  "#000000", "#434343", "#666666", "#999999", "#B7B7B7", "#CCCCCC", "#D9D9D9", "#EFEFEF", "#F3F3F3", "#FFFFFF",
  "#980000", "#FF0000", "#FF9900", "#FFFF00", "#00FF00", "#00FFFF", "#4A86E8", "#0000FF", "#9900FF", "#FF00FF",
  "#E6B8AF", "#F4CCCC", "#FCE5CD", "#FFF2CC", "#D9EAD3", "#D0E0E3", "#C9DAF8", "#CFE2F3", "#D9D2E9", "#EAD1DC",
  "#DD7E6B", "#EA9999", "#F9CB9C", "#FFE599", "#B6D7A8", "#A2C4C9", "#A4C2F4", "#9FC5E8", "#B4A7D6", "#D5A6BD",
  "#CC4125", "#E06666", "#F6B26B", "#FFD966", "#93C47D", "#76A5AF", "#6D9EEB", "#6FA8DC", "#8E7CC3", "#C27BA0",
  "#A61C00", "#CC0000", "#E69138", "#F1C232", "#6AA84F", "#45818E", "#3C78D8", "#3D85C6", "#674EA7", "#A64D79",
  "#85200C", "#990000", "#B45F06", "#BF9000", "#38761D", "#134F5C", "#1155CC", "#0B5394", "#351C75", "#741B47",
  "#5B0F00", "#660000", "#783F04", "#7F6000", "#274E13", "#0C343D", "#1C4587", "#073763", "#20124D", "#4C1130",
];

export default function WorkDocColorPicker({ value = "#000000", onChange, label = "Text Color" }) {
  const [open, setOpen] = useState(false);
  const [customColor, setCustomColor] = useState(value);

  useEffect(() => { setCustomColor(value); }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1 border border-gray-700 hover:bg-gray-800">
          <span className="w-3 h-3 rounded-full inline-block border border-gray-500" style={{ backgroundColor: value }} />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 bg-gray-900 border-gray-700" side="bottom" align="start">
        <div className="grid grid-cols-10 gap-1 mb-3">
          {PRESET_COLORS.map((color) => (
            <button
              key={color}
              className={`w-5 h-5 rounded-sm border ${color === value ? "border-white ring-1 ring-blue-400" : "border-gray-600"} hover:scale-110 transition-transform`}
              style={{ backgroundColor: color }}
              onClick={() => { onChange(color); setCustomColor(color); setOpen(false); }}
              title={color}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={customColor}
            onChange={(e) => { setCustomColor(e.target.value); onChange(e.target.value); }}
            className="w-8 h-8 p-0 border-0 cursor-pointer bg-transparent"
          />
          <span className="text-xs text-gray-400 font-mono">{customColor}</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
